
import React, { useState } from 'react';
import {
  RouteComponentProps,
  useLocation,
  Link as RouteLink,
} from 'react-router-dom';
import {
  Box,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';

import { GenderInput } from '@myfaro/ui-react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Party } from '@myfaro/domain';
import { useProfileContext } from '../../core/profile';
import { Who as WhoClass, Profile } from '../../core/types';
import { getAgeFromDateString, getDateStringFromAge } from '../../helpers';
import config from '../../core/context/config/config';
import { HasOwnCompany } from '../formControls/HasOwnCompany';
import { HasDependentChildren } from '../formControls/HasDependentChildren';

type WhoT = {
  onNext: () => void;
};

const { whoFields } = config;

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  subTitle: {
    marginBottom: theme.spacing(3),
    maxWidth: 400,
  },
  requiredText: {
    marginBottom: theme.spacing(3),
  },
  input: {
    marginBottom: theme.spacing(2),
    maxWidth: 400,
    width: '100%',
  },
  gridItem: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    flexDirection: 'column',
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
  },
  select: {
    maxWidth: 400,
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}));

function Who({ onNext }: WhoT) {
  const { t } = useTranslation();
  const classes = useStyles();
  const location: any = useLocation();
  const { useProfile, onChange, fieldError } = useProfileContext();
  const profile = useProfile<Profile>();
  const onChangeWho = onChange('who');
  const {
    name,
    dateOfBirth,
    email,
    mobile,
    socialStatus,
    civilStatus,
    gender,
    zip_code,
    hasOwnCompany,
    hasDependentChildren,
  } = profile.who;
  const [isTouched, setIsTouched] = useState({
    name: false,
    dateOfBirth: false,
    socialStatus: false,
    email: false,
    mobile: false,
    zip_code: false,
    gender: false,
    civilStatus: false,
  });

  return (
    <Box className={classes.body}>
      {location.state?.type === 'existingAccountError' && (
        <Box my={2}>
          <Alert severity='error'>
            <AlertTitle>{t('contact.errors.existingUser.title')}</AlertTitle>
            {t('contact.errors.existingUser.text')}{' '}
            <RouteLink to={{ pathname: '/', state: { type: 'prefillEmail' } }}>
              {t('contact.errors.existingUser.link')}
            </RouteLink>
          </Alert>
        </Box>
      )}
      {location.state?.type === 'existingDossierError' && (
        <Box my={2}>
          <Alert severity='error'>
            <AlertTitle>{t('contact.errors.existingDossier.title')}</AlertTitle>
            {t('contact.errors.existingDossier.text')}
          </Alert>
        </Box>
      )}
      {location.state?.type === 'invalidTokenError' && (
        <Box my={2}>
          <Alert severity='error'>
            <AlertTitle>{t('contact.errors.invalidToken.title')}</AlertTitle>
            {t('contact.errors.invalidToken.text')}{' '}
            <RouteLink to={{ pathname: '/', state: { type: 'prefillEmail' } }}>
              {t('contact.errors.invalidToken.link')}
            </RouteLink>
          </Alert>
        </Box>
      )}
      <Typography variant='h5' className={classes.title}>
        {t('who.title')}
      </Typography>
      <Typography
        variant='body2'
        color={'textSecondary'}
        className={classes.subTitle}>
        {t('who.subTitle')}
      </Typography>
      <Typography
        variant='caption'
        color={'textSecondary'}
        className={classes.requiredText}>
        {t('requiredText')}
      </Typography>
      <Grid container spacing={2}>
        {whoFields.includes('name') && (
          <Grid className={classes.gridItem} item xs={12} md={6}>
            <TextField
              required
              label={t('label.name')}
              variant={'outlined'}
              value={name}
              onChange={event => onChangeWho({ name: event.target.value })}
              onBlur={() =>
                !isTouched.name &&
                setIsTouched(state => ({ ...state, name: true }))
              }
              className={classes.input}
              error={Boolean(isTouched.name && fieldError('who', 'name'))}
              helperText={isTouched.name && fieldError('who', 'name')}
            />
          </Grid>
        )}
        {whoFields.includes('dateOfBirth') && (
          <Grid item className={classes.gridItem} xs={12} md={6}>
            <TextField
              required
              label={t('label.age')}
              variant={'outlined'}
              type='number'
              value={getAgeFromDateString(dateOfBirth)}
              onChange={event => {
                onChangeWho({
                  dateOfBirth: getDateStringFromAge(event.target.value),
                });
              }}
              onBlur={() =>
                !isTouched.dateOfBirth &&
                setIsTouched(state => ({ ...state, dateOfBirth: true }))
              }
              className={classes.input}
              error={Boolean(
                isTouched.dateOfBirth && fieldError('who', 'dateOfBirth'),
              )}
              helperText={
                isTouched.dateOfBirth && fieldError('who', 'dateOfBirth')
              }
            />
          </Grid>
        )}
        {whoFields.includes('gender') && (
          <Grid className={classes.gridItem} item xs={12} md={6}>
            <InputLabel shrink required className={classes.inputLabel}>
              {t('who.gender')}
            </InputLabel>
            <Box className={classes.input}>
              <GenderInput
                selected={gender}
                onChange={(e: any, value: Party.gender) => {
                  onChangeWho({
                    gender: value,
                  });
                }}
              />
            </Box>
          </Grid>
        )}
        {whoFields.includes('zip_code') && (
          <Grid className={classes.gridItem} item xs={12} md={6}>
            <TextField
              required
              name='zip_code'
              type='number'
              label={t('who.zip_code')}
              variant='outlined'
              value={zip_code}
              onBlur={() => 
                !isTouched.zip_code && setIsTouched(state => ({ ...state, zip_code: true }))
              }
              error={Boolean(isTouched.zip_code && fieldError('who', 'zip_code'))}
              helperText={
                isTouched.zip_code && fieldError('who', 'zip_code')
              }
              onChange={event =>
                onChangeWho({ ...profile.who, zip_code: event.target.value })
              }
              className={classes.input}
            />
          </Grid>
        )}
        {profile.result && (
          <>
            <Grid className={classes.gridItem} item xs={12} md={6}>
              <TextField
                required
                name='email'
                label={t('E-mailadres')}
                variant='outlined'
                value={email}
                onBlur={() =>
                  !isTouched.email &&
                  setIsTouched(state => ({ ...state, email: true }))
                }
                error={Boolean(isTouched.email && fieldError('who', 'email'))}
                helperText={isTouched.email && fieldError('who', 'email')}
                onChange={event =>
                  onChangeWho({
                    email: event.target.value,
                  })
                }
                className={classes.input}
              />
            </Grid>

            <Grid className={classes.gridItem} item xs={12} md={6}>
              <TextField
                required
                name='mobile'
                label={t('form.mobile')}
                variant='outlined'
                value={mobile || profile.contact.mobile}
                onBlur={() =>
                  !isTouched.mobile &&
                  setIsTouched(state => ({ ...state, mobile: true }))
                }
                error={Boolean(isTouched.mobile && fieldError('who', 'mobile'))}
                helperText={isTouched.mobile && fieldError('who', 'mobile')}
                onChange={event => onChangeWho({ mobile: event.target.value })}
                className={classes.input}
              />
            </Grid>
          </>
        )}
        {whoFields.includes('socialStatus') && (
          <Grid className={classes.gridItem} item xs={12} md={6}>
            <InputLabel shrink required className={classes.inputLabel}>
              {t('label.socialStatus')}
            </InputLabel>
            <Select
              variant={'outlined'}
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              className={classes.select}
              value={socialStatus}
              onChange={event =>
                onChangeWho({
                  socialStatus: event.target.value as Party.socialStatus,
                })
              }
              onBlur={() =>
                !isTouched.socialStatus &&
                setIsTouched(state => ({ ...state, socialStatus: true }))
              }
              error={Boolean(
                isTouched.socialStatus && fieldError('who', 'socialStatus'),
              )}>
              <MenuItem value={Party.socialStatus._13}>
                {t('label.independent')}
              </MenuItem>
              <MenuItem value={Party.socialStatus._10}>
                {t('label.freeProfession')}
              </MenuItem>
              <MenuItem value={Party.socialStatus._01}>
                {t('label.civilServant')}
              </MenuItem>
              <MenuItem value={Party.socialStatus._36}>
                {t('label.unemployed')}
              </MenuItem>
              <MenuItem value={Party.socialStatus._99}>
                {t('label.other')}
              </MenuItem>
            </Select>
          </Grid>
        )}
        {whoFields.includes('hasOwnCompany') && (
          <Grid className={classes.gridItem} item xs={12} md={6}>
            <InputLabel shrink required className={classes.inputLabel}>
              {t('label.hasOwnCompany')}
            </InputLabel>
            <Box className={classes.input}>
              <HasOwnCompany
                value={hasOwnCompany}
                onChange={(event: any, value: boolean) =>
                  onChangeWho({ ...profile.who, hasOwnCompany: value })
                }
              />
            </Box>
          </Grid>
        )}
        {whoFields.includes('civilStatus') && (
          <Grid className={classes.gridItem} item xs={12} md={6}>
            <InputLabel shrink required className={classes.inputLabel}>
              {t('label.civilStatus')}
            </InputLabel>
            <Select
              variant={'outlined'}
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              className={classes.select}
              value={civilStatus}
              onChange={event =>
                onChangeWho({
                  civilStatus: event.target.value as Party.civilStatus,
                })
              }
              onBlur={() =>
                !isTouched.civilStatus &&
                setIsTouched(state => ({ ...state, civilStatus: true }))
              }
              error={Boolean(
                isTouched.civilStatus && fieldError('who', 'civilStatus'),
              )}>
              <MenuItem value={Party.civilStatus._2}>
                {t('label.single')}
              </MenuItem>
              <MenuItem value={Party.civilStatus._1}>
                {t('label.married')}
              </MenuItem>
              <MenuItem value={Party.civilStatus._6}>
                {t('label.livingTogether')}
              </MenuItem>
              <MenuItem value={Party.civilStatus._99}>
                {t('label.others')}
              </MenuItem>
            </Select>
          </Grid>
        )}
        {whoFields.includes('hasDependentChildren') && (
          <Grid className={classes.gridItem} item xs={12} md={6}>
            <InputLabel shrink className={classes.inputLabel}>
              {t('who.hasDependentChildren')}
            </InputLabel>
            <Box className={classes.input}>
              <HasDependentChildren
                value={hasDependentChildren}
                onChange={(event: any, value: boolean) =>
                  onChangeWho({ ...profile.who, hasDependentChildren: value })
                }
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function WhoR({ history }: RouteComponentProps) {
  return <Who onNext={() => history.push('/what')} />;
}

export default WhoR;
